import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import { Button, Icon, Message } from 'semantic-ui-react';
import axios from 'axios';
import CapCallDetailApproval, { capCallTechnicalRequirementSections } from '../PdfLetters/CapCallDetailApproval';
import CapCallDetailRejection from '../PdfLetters/CapCallDetailRejection';
import { setPage } from '../../redux/appSlice';
import { setLetter } from "../../redux/projectSlice";

const useTitle = (title) => {
    useEffect(() => {
        const prevTitle = document.title;
        document.title = title;
        return () => {
            document.title = prevTitle;
        }
    }, [title])
}

const REJECTION_CONCLUSIONS_TEMPLATE = 'The project application’s main strengths are:\n- \n- \n- \n\nThe project application’s main weaknesses are:\n- \n- \n- ';

// decision: 'APPROVAL' | 'REJECTION'
const CapCallNotificationLetterService = ({ decision }) => {
    const projectState = useSelector(state => state.project);
    const appState = useSelector(state => state.app);
    const dispatch = useDispatch();
    const componentRef = useRef();
    const API_MDB = process.env.REACT_APP_BASE_API_URL_MDB;
    const options = {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
    }
    const handlePrint = useReactToPrint({
        content: () => componentRef.current
    });

    const updateLetterDataAndPrint = async () => {
        const resLetter = await axios.patch(API_MDB + '/letters/' + projectState.letter._id, projectState.letter, options);
        if (resLetter.status === 204) {
            handlePrint();
        }
    }

    // Latest instance first, as a checklist can be filled several times
    const getSortedChecklists = () => projectState.projectChecklists.toSorted((a, b) => b.id - a.id);

    const getChecklistByName = (name) => getSortedChecklists().find(checklist => checklist.name === name);

    const getTextInputs = (checklist) => (checklist?.detail?.components ?? [])
        .filter(component => component.type === 'TEXT_INPUT')
        .sort((a, b) => a.position - b.position);

    const getRejectionConclusions = (inputs) => {
        const getAnswer = (match) => inputs.find(item => match.test(item.programmeMetadata?.question))?.instanceMetadata?.explanation;
        const strengths = getAnswer(/strength/i);
        const weaknesses = getAnswer(/weakness/i);
        if (strengths || weaknesses) {
            return `The project application’s main strengths are:\n${strengths ?? ''}\n\nThe project application’s main weaknesses are:\n${weaknesses ?? ''}`;
        }
        return inputs.map(item => item.instanceMetadata?.explanation).filter(Boolean).join('\n\n');
    }

    const getCapCallDataFromChecklists = () => {
        const capCallData = {};

        const technicalRequirements = getTextInputs(getChecklistByName(projectState.capCallTechnicalRequirements));
        capCallTechnicalRequirementSections.forEach((section, index) => {
            const input = technicalRequirements.find(item => section.match.test(item.programmeMetadata?.question)) ?? technicalRequirements[index];
            capCallData[section.key] = input?.instanceMetadata?.explanation ?? '';
        });

        const rejectionReasons = getTextInputs(getChecklistByName(projectState.capCallRejectionReasons));
        capCallData.conclusions = getRejectionConclusions(rejectionReasons) || REJECTION_CONCLUSIONS_TEMPLATE;

        return capCallData;
    }

    const reloadFromChecklists = () => {
        dispatch(setLetter({ ...projectState.letter, capCallData: getCapCallDataFromChecklists() }));
    }

    useEffect(() => {
        const getLetterData = async () => {
            let letter = projectState.letter;
            if (!letter.projectNumber) {
                const resLetter = await axios.get(API_MDB + '/letters/' + projectState.project.customIdentifier, options);
                letter = resLetter.data;
                if (letter?.projectNumber === undefined) {
                    const newLetter = {
                        projectNumber: projectState.project.customIdentifier,
                        callNumber: projectState.project.callSettings.callId,
                    };
                    const resNewLetter = await axios.post(API_MDB + '/letters', newLetter, options);
                    letter = resNewLetter.data;
                }
            }
            if (!letter.capCallData) {
                letter = { ...letter, capCallData: getCapCallDataFromChecklists() };
            }
            dispatch(setLetter(letter));
        }
        getLetterData().catch(error => console.error('Error fetching cap call letter data', error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useTitle('P' + projectState.project.programmePriority?.code + '_' + projectState.project.customIdentifier + '_' + projectState.project.acronym + '_CAP_' + decision);

    const checklistName = decision === 'APPROVAL' ? projectState.capCallTechnicalRequirements : projectState.capCallRejectionReasons;
    const checklistMissing = getChecklistByName(checklistName) === undefined;

    return (
        <div>
            <div className='menu'>
                <Button.Group>
                    <Button icon onClick={() => dispatch(setPage('Projects'))} color='blue' labelPosition='left'>
                        <Icon name='arrow left' />
                        Back
                    </Button>
                    <Button.Or />
                    <Button icon onClick={reloadFromChecklists} color='grey' labelPosition='left'>
                        <Icon name='refresh' />
                        Reload from Jems checklists
                    </Button>
                    <Button.Or />
                    <Button icon onClick={updateLetterDataAndPrint} color='blue' labelPosition='right'>
                        <Icon name='save' />
                        Save / Print
                    </Button>
                </Button.Group>
            </div>
            {appState.jems32 ? <Message warning><Message.Header>You're connected to Jems32 server.</Message.Header>Collected data may not reflect the live server please use <a href="http://jemsreports.nweurope.eu/" target="_blank" rel="noopener noreferrer">Jems Live Server</a></Message> : undefined}
            {checklistMissing ? <Message warning><Message.Header>No "{checklistName}" checklist found for this project</Message.Header>The annex can still be typed below.</Message> : undefined}
            <div ref={componentRef}>
                {decision === 'APPROVAL' ? <CapCallDetailApproval /> : <CapCallDetailRejection />}
            </div>
        </div>
    );
}

export default CapCallNotificationLetterService;
